import { getGrpcPort } from '../../../shared/api/grpc/transport';
import {
  EXPORT_READINESS_SUMMARY_VALIDATION_ID,
  normalizeValidationId,
} from '../../../shared/config/validationCatalog';

function getValueCaseInsensitive(source: Record<string, any>, key: string): any {
  const target = key.toLowerCase();
  const matched = Object.keys(source).find((k) => k.toLowerCase() === target);
  return matched ? source[matched] : undefined;
}

export function getSystemField(item: Record<string, any>, ...candidateKeys: string[]): string {
  const sources: Record<string, any>[] = [item];
  if (item.__props && typeof item.__props === 'object') {
    sources.push(item.__props);
  }
  if (item.props && typeof item.props === 'object') {
    sources.push(item.props);
  }

  for (const source of sources) {
    for (const key of candidateKeys) {
      const direct = source[key];
      if (direct !== undefined && direct !== null && String(direct).trim() !== '') {
        return String(direct);
      }

      const ci = getValueCaseInsensitive(source, key);
      if (ci !== undefined && ci !== null && String(ci).trim() !== '') {
        return String(ci);
      }
    }
  }

  return '';
}

export function isUnknownComMemberError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('0x80020006') || message.toLowerCase().includes('unknown name');
}

export function normalizeTransportError(errorText: string | undefined, context: 'collect' | 'export'): string {
  const fallback = context === 'collect'
    ? 'PipeEX could not load the piping systems'
    : 'PipeEX could not export the PCF files';
  const msg = (errorText || '').trim();
  if (!msg) {
    return fallback;
  }

  const lowered = msg.toLowerCase();
  if (lowered.includes('failed to fetch') || lowered.includes('networkerror') || lowered.includes('network error')) {
    const port = getGrpcPort();
    return `PipeEX cannot connect to Revit${port ? ` on port ${port}` : ''}. Reload the PipeEX add-in, then try again.`;
  }

  return msg;
}

export function createExportTraceId(): string {
  const now = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 8);
  return `pcf-${now}-${random}`;
}

function isExportTraceConsoleEnabled(): boolean {
  return typeof window !== 'undefined' && (window as any).__PIPEEX_EXPORT_TRACE === true;
}

export function logExportDebug(message: string, payload?: unknown): void {
  if (isExportTraceConsoleEnabled()) {
    console.info(message, payload);
  }
}

export function getExportTraceNowMs(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}

export function getExportTraceElapsedMs(startedAtMs: number): number {
  return Math.max(0, Math.round(getExportTraceNowMs() - startedAtMs));
}

export function getExportErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function scheduleAfterInitialPaint(callback: () => void): () => void {
  let frameId: number | null = null;
  let timeoutId: number | null = null;
  let cancelled = false;
  const browserWindow = typeof window === 'undefined' ? null : window;

  if (!browserWindow) {
    return () => {
      cancelled = true;
    };
  }

  const scheduleCallback = () => {
    timeoutId = browserWindow.setTimeout(() => {
      timeoutId = null;
      if (!cancelled) {
        callback();
      }
    }, 0);
  };

  if (typeof browserWindow.requestAnimationFrame !== 'function') {
    timeoutId = browserWindow.setTimeout(scheduleCallback, 0);
  } else {
    frameId = browserWindow.requestAnimationFrame(() => {
      frameId = null;
      scheduleCallback();
    });
  }

  return () => {
    cancelled = true;
    if (frameId !== null && typeof browserWindow.cancelAnimationFrame === 'function') {
      browserWindow.cancelAnimationFrame(frameId);
    }
    if (timeoutId !== null) {
      browserWindow.clearTimeout(timeoutId);
    }
  };
}

export function normalizeSystemNameToken(value: string | undefined): string {
  return String(value || '').trim().toLowerCase();
}

export function getSessionExportCountKey(systemName: string | undefined): string {
  return normalizeSystemNameToken(systemName);
}

export function isGlobalSystemScopeName(value: string | undefined): boolean {
  const normalized = normalizeSystemNameToken(value);
  return normalized === 'all systems' || normalized === '(unassigned system)';
}

export function parseSystemNames(raw: string | undefined): string[] {
  const value = String(raw || '').trim();
  if (!value) {
    return [];
  }

  const multipleSystemsPrefix = 'Multiple systems (';
  const normalized =
    value.startsWith(multipleSystemsPrefix) && value.endsWith(')')
      ? value.slice(multipleSystemsPrefix.length, -1)
      : value;

  return normalized
    .split(/[;,|]+/)
    .map((entry) => entry.replace('...', '').trim())
    .filter((entry) => entry.length > 0);
}

function readIssueAdditionalDataValue(issue: { AdditionalData?: Record<string, string> }, key: string): string {
  const data = issue.AdditionalData;
  if (!data) {
    return '';
  }

  const direct = data[key];
  if (typeof direct === 'string' && direct.trim()) {
    return direct;
  }

  const ci = getValueCaseInsensitive(data as Record<string, any>, key);
  return typeof ci === 'string' ? ci : '';
}

export function collectIssueSystemNames(issue: { AdditionalData?: Record<string, string> }): Set<string> {
  const tokens = new Set<string>();
  const candidateKeys = ['systemName', 'sourceSystemName', 'systemTypeName', 'system', 'sourceSystemNames'];

  for (const key of candidateKeys) {
    const rawValue = readIssueAdditionalDataValue(issue, key);
    parseSystemNames(rawValue).forEach((name) => {
      const normalized = normalizeSystemNameToken(name);
      if (normalized && !isGlobalSystemScopeName(normalized)) {
        tokens.add(normalized);
      }
    });
  }

  return tokens;
}

export function addIssueElementIds(target: Set<string>, issue: { ElementIds?: string[] }): void {
  if (!Array.isArray(issue.ElementIds)) {
    return;
  }

  for (const id of issue.ElementIds) {
    const normalized = String(id).trim();
    if (normalized) {
      target.add(normalized);
    }
  }
}

export function isErrorSeverity(severity: string | undefined): boolean {
  const normalized = String(severity || '').trim().toLowerCase();
  return normalized === 'critical' || normalized === 'error';
}

export function isWarningSeverity(severity: string | undefined): boolean {
  const normalized = String(severity || '').trim().toLowerCase();
  return normalized === 'warning';
}

export function isActionableValidationIssue(
  check: { ValidationId?: number },
  issue: { ValidationId?: number }
): boolean {
  const checkValidationId = normalizeValidationId(Number(check.ValidationId || 0));
  const issueValidationId = normalizeValidationId(Number(issue.ValidationId || check.ValidationId || 0));

  return (
    checkValidationId !== EXPORT_READINESS_SUMMARY_VALIDATION_ID &&
    issueValidationId !== EXPORT_READINESS_SUMMARY_VALIDATION_ID
  );
}

async function tryWriteHostTrace(_payload: Record<string, unknown>): Promise<void> {
  return;
}

export async function emitExportTrace(
  traceId: string,
  step: string,
  payload: Record<string, unknown> = {}
): Promise<void> {
  const tracePayload = {
    traceId,
    layer: 'useExportState',
    step,
    timestamp: new Date().toISOString(),
    ...payload,
  };

  logExportDebug('[useExportState][trace]', tracePayload);
  await tryWriteHostTrace(tracePayload);
}
